import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Home, Calendar, DollarSign, Users, TrendingUp, Plus,
  Settings, BarChart3, Bell, LogOut, Eye, Edit2, Trash2, MessageSquare, ClipboardList, CreditCard,
  X, User, MapPin, Phone, Mail, Hash, ChevronRight, Package, Sparkles, CheckCircle2, AlertTriangle, HelpCircle
} from 'lucide-react';
import FAQFeedback from '../components/FAQFeedback';
import PropertyCalendar from '../components/PropertyCalendar';
import HostMessagingPanel from '../components/HostMessagingPanel';
import TaskManagement from '../components/TaskManagement';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import DealSourcing from '../components/DealSourcing';
import RoleSwitcher from '../components/RoleSwitcher';
import PayoutsTab from '../components/PayoutsTab';
import InventoryManagement from '../components/InventoryManagement';
import FinancialInsights from '../components/FinancialInsights';
import ReferralDashboard from '../components/ReferralDashboard';
import FoundingCohortBadge from '../components/FoundingCohortBadge';
import { propertyAPI, bookingAPI, messagesAPI, providerAPI, tasksAPI, cleaningAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const HostDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, getAuthToken, hasHostRole, logout, loading: authLoading } = useAuth();

  // Tab state is derived from the URL — single source of truth for browser back/forward
  const activeTab = searchParams.get('tab') || 'overview';

  // When user clicks a tab, push a new URL (creates browser history entry)
  const setActiveTab = useCallback((tab) => {
    setSearchParams({ tab }, { replace: false });
  }, [setSearchParams]);

  // Increment each time user navigates TO the messages tab — signals HostMessagingPanel to re-fetch
  const [messagesRefreshKey, setMessagesRefreshKey] = useState(0);
  const prevActiveTabRef = useRef(activeTab);
  useEffect(() => {
    if (activeTab === 'messages' && prevActiveTabRef.current !== 'messages') {
      setMessagesRefreshKey(k => k + 1);
    }
    prevActiveTabRef.current = activeTab;
  }, [activeTab]);
  const [bookingsView, setBookingsView] = useState('list');
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  // Acknowledged state so the red badge clears as soon as the user opens
  // the Messages tab, and only reappears when a NEW message arrives.
  const [messagesAck, setMessagesAck] = useState(false);
  const prevUnreadRef = useRef(0);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetail, setShowBookingDetail] = useState(false);
  const [showAnalyticsDrawer, setShowAnalyticsDrawer] = useState(false);
  const [highlightedBookingId, setHighlightedBookingId] = useState(null);
  const [dismissedEvents, setDismissedEvents] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('bc_dismissed_activity') || '[]')); }
    catch { return new Set(); }
  });

  // Dismiss a recent activity event (notification-like behavior)
  const dismissEvent = useCallback((eventId) => {
    setDismissedEvents(prev => {
      const next = new Set([...prev, eventId]);
      localStorage.setItem('bc_dismissed_activity', JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Navigate to a booking from Recent Activity
  const navigateToBooking = useCallback((bookingId) => {
    const booking = bookings.find(b => b._id === bookingId);
    if (booking) {
      setActiveTab('bookings');
      setBookingsView('list');
      setHighlightedBookingId(bookingId);
      // Auto-open the booking detail after a short delay to let the tab render
      setTimeout(() => {
        setSelectedBooking(booking);
        setShowBookingDetail(true);
      }, 300);
      // Clear highlight after 5 seconds
      setTimeout(() => setHighlightedBookingId(null), 5000);
    }
  }, [bookings, setActiveTab]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (token) {
        const data = await messagesAPI.getUnreadCount(token);
        setUnreadCount(data.hostUnread || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [getAuthToken]);

  const handleUnreadChange = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (token) {
        const data = await messagesAPI.getUnreadCount(token);
        const newCount = data.hostUnread || 0;
        if (newCount > prevUnreadRef.current && activeTab !== 'messages') {
          toast({ title: '💬 New message', description: 'You have a new message.' });
          setMessagesAck(false); // new message, re-show badge
        }
        prevUnreadRef.current = newCount;
        setUnreadCount(newCount);
      }
    } catch {}
  }, [getAuthToken, activeTab, toast]);

  // Acknowledge unread badge when user opens the Messages tab
  useEffect(() => {
    if (activeTab === 'messages') {
      setMessagesAck(true);
      setUnreadCount(0);
      prevUnreadRef.current = 0;
    }
  }, [activeTab]);

  // Redirect if not a host — but wait for auth to finish loading first
  useEffect(() => {
    if (!authLoading && !hasHostRole()) {
      navigate('/guest/browse', { replace: true });
    }
  }, [authLoading, hasHostRole, navigate]);

  useEffect(() => {
    if (!authLoading && hasHostRole()) {
      fetchData();
      fetchUnreadCount();

      // Poll for unread count every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, authLoading, fetchUnreadCount]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const [propertiesData, bookingsData] = await Promise.all([
        propertyAPI.getHostProperties(token),
        bookingAPI.getHostBookings(token)
      ]);
      setProperties(propertiesData);
      setBookings(bookingsData);
      if (propertiesData.length > 0 && !selectedProperty) {
        setSelectedProperty(propertiesData[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    
    try {
      const token = getAuthToken();
      await propertyAPI.deleteProperty(propertyId, token);
      toast({ title: 'Success', description: 'Property deleted successfully' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete property', variant: 'destructive' });
    }
  };

  const handleToggleStatus = async (propertyId, currentStatus) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    try {
      const token = getAuthToken();
      await propertyAPI.updatePropertyStatus(propertyId, newStatus, token);
      toast({ title: 'Success', description: `Property ${newStatus === 'published' ? 'published' : 'unpublished'} successfully` });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update property status', variant: 'destructive' });
    }
  };

  const handleBookingAction = async (bookingId, action) => {
    try {
      const token = getAuthToken();
      await bookingAPI.updateBookingStatus(bookingId, action, token);
      toast({ title: 'Success', description: `Booking ${action}` });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update booking', variant: 'destructive' });
    }
  };

  // Distinct flow for same-day late-window booking requests. The
  // dedicated endpoints write a short payment deadline (host-approve)
  // and a tagged cancellation reason (host-decline), and send the
  // appropriate guest email — using the generic status PATCH would skip
  // all of that.
  const handleApprovalDecision = async (bookingId, decision) => {
    try {
      const token = getAuthToken();
      if (decision === 'approve') {
        await bookingAPI.hostApproveBooking(bookingId, token);
        toast({ title: 'Request approved', description: 'Guest has been emailed a payment link.' });
      } else {
        await bookingAPI.hostDeclineBooking(bookingId, token);
        toast({ title: 'Request declined', description: 'Guest has been notified.' });
      }
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update the request',
        variant: 'destructive',
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const todayStr = new Date().toISOString().slice(0, 10);

  const hostStats = {
    totalProperties: properties.length,
    publishedProperties: properties.filter(p => p.status === 'published').length,
    draftProperties: properties.filter(p => p.status === 'draft').length,
    activeBookings: bookings.filter(b => b.status === 'confirmed' && !(b.checkOut && b.checkOut < todayStr)).length,
    pendingBookings: bookings.filter(b => (b.status === 'pending' || b.status === 'pending_payment') && !(b.checkOut && b.checkOut < todayStr)).length,
    monthlyRevenue: bookings.filter(b => b.status === 'confirmed').reduce((sum, b) => sum + (b.pricing?.total || 0), 0),
    occupancyRate: 0
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-gray-700 hover:text-[#1F3A68] font-medium">
                Browse Properties
              </Link>
              <Link to="/services" className="text-gray-700 hover:text-[#1F3A68] font-medium">
                Find Vendors
              </Link>
              <Link to="/host/dashboard" className="text-[#1F3A68] font-semibold">
                Host Dashboard
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <RoleSwitcher />
              <Button 
                variant="outline" 
                size="icon" 
                className="relative"
                onClick={() => setActiveTab('messages')}
                data-testid="bell-icon-btn"
              >
                <Bell size={20} />
                {unreadCount > 0 && !messagesAck && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-[#CB3B3B]"
                    data-testid="bell-unread-badge"
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
              </Button>
              <Button variant="outline" size="icon" onClick={() => navigate('/settings')}>
                <Settings size={20} />
              </Button>
              <Button onClick={handleLogout} className="bg-[#1F3A68] hover:bg-[#1F3A68]/90" aria-label="Logout">
                <LogOut size={16} className="md:mr-2" />
                <span className="hidden md:inline">Logout</span>
              </Button>
            </div>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <div className="flex items-start gap-3 flex-wrap">
            <h1 className="text-4xl font-bold text-[#1F3A68] mb-2">{user?.lastLoginAt ? 'Welcome back' : 'Welcome'}, {user?.name?.split(' ')[0] || 'Host'}!</h1>
            {user?.foundingCohort && (
              <FoundingCohortBadge size="md" className="mt-2" />
            )}
          </div>
          <p className="text-gray-600 text-lg">Manage your properties and grow your rental business</p>
        </div>

        {/* Founding-cohort hero card (only for first 100 hosts) */}
        {user?.foundingCohort && (
          <FoundingCohortBadge
            variant="card"
            startedAt={user.foundingCohortStartedAt}
            className="mb-6"
          />
        )}

        {/* Stats Overview */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-[#1F3A68] to-[#1F3A68]/80 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Home size={24} />
                <TrendingUp size={20} className="text-green-300" />
              </div>
              <div className="text-3xl font-bold mb-1">{hostStats.totalProperties}</div>
              <div className="text-white/80">Active Properties</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#CB3B3B] to-[#CB3B3B]/80 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Calendar size={24} />
                {hostStats.pendingBookings > 0 && (
                  <span className="bg-white text-[#CB3B3B] text-xs font-bold px-2 py-1 rounded-full">
                    {hostStats.pendingBookings} pending
                  </span>
                )}
              </div>
              <div className="text-3xl font-bold mb-1">{hostStats.activeBookings}</div>
              <div className="text-white/80">Active Bookings</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <DollarSign size={24} />
                <TrendingUp size={20} className="text-green-300" />
              </div>
              <div className="text-3xl font-bold mb-1">£{hostStats.monthlyRevenue.toLocaleString()}</div>
              <div className="text-white/80">Total Revenue</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <BarChart3 size={24} />
              </div>
              <div className="text-3xl font-bold mb-1">{hostStats.publishedProperties}</div>
              <div className="text-white/80">Published</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex flex-wrap justify-center w-full bg-white px-3 sm:px-4 py-2 rounded-xl shadow-md gap-1.5">
            <TabsTrigger value="overview" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none">
              Overview
            </TabsTrigger>
            <TabsTrigger value="properties" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none">
              Properties
            </TabsTrigger>
            <TabsTrigger value="bookings" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none">
              Bookings
            </TabsTrigger>
            <TabsTrigger
              value="messages"
              className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg relative whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none"
              data-testid="messages-tab"
            >
              Messages
              {unreadCount > 0 && !messagesAck && (
                <Badge
                  variant="destructive"
                  className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-[#CB3B3B]"
                  data-testid="messages-tab-unread-badge"
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="tasks" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none">
              Tasks
            </TabsTrigger>
            <TabsTrigger value="deals" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="deals-tab">
              Deals
            </TabsTrigger>
            <TabsTrigger value="payouts" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="payouts-tab">
              <CreditCard size={14} className="mr-1" />
              Payouts
            </TabsTrigger>
            <TabsTrigger value="inventory" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none">
              Inventory
            </TabsTrigger>
            <TabsTrigger value="insights" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none">
              <TrendingUp size={14} className="mr-1" />
              Insights
            </TabsTrigger>
            <TabsTrigger
              value={activeTab}
              className="px-3 py-1.5 text-xs sm:text-sm bg-[#1F3A68]/10 text-[#1F3A68] hover:bg-[#1F3A68] hover:text-white rounded-lg"
              onClick={(e) => { e.preventDefault(); setShowAnalyticsDrawer(true); }}
            >
              <BarChart3 size={14} className="mr-1" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="referrals" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="referrals-tab">
              <Sparkles size={14} className="mr-1" />
              Refer &amp; Earn
            </TabsTrigger>
            <TabsTrigger value="faq" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none">
              <HelpCircle size={14} className="mr-1" />
              FAQ
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-[#1F3A68]">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-4">
                  <Button 
                    className="h-24 flex flex-col gap-2 bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                    onClick={() => navigate('/host/create-property')}
                  >
                    <Plus size={24} />
                    <span>Add Property</span>
                  </Button>
                  <Button variant="outline" className="h-24 flex flex-col gap-2" onClick={() => { setActiveTab('bookings'); setBookingsView('calendar'); }}>
                    <Calendar size={24} />
                    <span>Manage Calendar</span>
                  </Button>
                  <Button variant="outline" className="h-24 flex flex-col gap-2" onClick={() => setActiveTab('messages')}>
                    <MessageSquare size={24} />
                    <span>Messages</span>
                  </Button>
                  <Button variant="outline" className="h-24 flex flex-col gap-2" onClick={() => setActiveTab('tasks')}>
                    <ClipboardList size={24} />
                    <span>Tasks</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Properties Summary — always visible in Overview */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-[#1F3A68]">Your Properties</CardTitle>
                <div className="flex items-center gap-2">
                  {properties.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#1F3A68] text-xs"
                      onClick={() => setActiveTab('properties')}
                    >
                      View all →
                    </Button>
                  )}
                  <Button
                    size="sm"
                    className="bg-[#1F3A68] hover:bg-[#1F3A68]/90 text-xs h-8"
                    onClick={() => navigate('/host/create-property')}
                  >
                    <Plus size={14} className="mr-1" />
                    Add Property
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {properties.length === 0 ? (
                  <div className="text-center py-10">
                    <Home size={48} className="mx-auto text-gray-300 mb-3" />
                    <h3 className="text-lg font-bold text-gray-700 mb-1">No Properties Yet</h3>
                    <p className="text-gray-500 text-sm mb-4">Get started by creating your first property listing</p>
                    <Button className="bg-[#1F3A68] hover:bg-[#1F3A68]/90" onClick={() => navigate('/host/create-property')}>
                      <Plus size={16} className="mr-2" />
                      Create Your First Property
                    </Button>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {properties.map(property => (
                      <div
                        key={property._id}
                        className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-[#1F3A68]/30 hover:bg-[#1F3A68]/5 transition-all cursor-pointer"
                        onClick={() => navigate(`/property/${property._id}`)}
                      >
                        {property.images?.length > 0 ? (
                          <img
                            src={property.images[0]}
                            alt={property.title}
                            className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Home size={24} className="text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <p className="font-semibold text-sm text-gray-900 truncate">{property.title}</p>
                          </div>
                          <p className="text-xs text-gray-500 truncate mb-1">{property.location?.city || property.location?.address || '—'}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-[#1F3A68]">£{property.pricing?.nightlyRate || 0}<span className="font-normal text-xs text-gray-500">/night</span></span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                              property.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {property.status === 'published' ? 'Live' : 'Draft'}
                            </span>
                          </div>
                          {property.insurance?.hasInsurance ? (
                            property.insurance?.expiry && new Date(property.insurance.expiry) < new Date() ? (
                              <p className="text-[10px] text-red-600 mt-1">Insurance expired ({property.insurance.expiry})</p>
                            ) : (
                              <p className="text-[10px] text-gray-400 mt-1">Insurance on file{property.insurance?.expiry ? ` · expires ${property.insurance.expiry}` : ''}</p>
                            )
                          ) : (
                            <p className="text-[10px] text-amber-600 mt-1">Insurance not recorded</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {properties.length > 0 && (
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-[#1F3A68]">Pending Bookings</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {bookings.filter(b => b.status === 'pending' || b.status === 'pending_payment' || b.status === 'pending_host_approval').length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No pending bookings</p>
                    ) : (
                      <div className="space-y-3">
                        {bookings.filter(b => b.status === 'pending' || b.status === 'pending_payment' || b.status === 'pending_host_approval').slice(0, 5).map(booking => {
                          const isApprovalRequest = booking.status === 'pending_host_approval';
                          const isAwaitingPayment = booking.status === 'pending_payment';
                          const cardClass = isApprovalRequest
                            ? 'bg-rose-50 border-rose-200 hover:border-rose-400'
                            : isAwaitingPayment
                              ? 'bg-blue-50 border-blue-200 hover:border-blue-400'
                              : 'bg-amber-50 border-amber-200 hover:border-amber-400';
                          const badgeClass = isApprovalRequest
                            ? 'bg-rose-100 text-rose-700'
                            : isAwaitingPayment
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-amber-100 text-amber-700';
                          const badgeText = isApprovalRequest
                            ? 'Same-day request'
                            : isAwaitingPayment
                              ? 'Awaiting Payment'
                              : 'Pending';
                          return (
                            <div key={booking._id} className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md hover:scale-[1.01] ${cardClass}`} onClick={() => navigateToBooking(booking._id)}>
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-semibold">{booking.guestName}</p>
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badgeClass}`}>
                                      {badgeText}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600">{booking.propertyTitle}</p>
                                  <p className="text-sm text-gray-500">{booking.checkIn} — {booking.checkOut}</p>
                                  {isApprovalRequest && (
                                    <p className="text-xs text-rose-700 mt-1">
                                      Guest wants to check in today, after your usual check-in time. Decide before 23:00.
                                    </p>
                                  )}
                                  {isAwaitingPayment && booking.paymentDeadline && (
                                    <p className="text-xs text-blue-600 mt-1">
                                      Payment due: {new Date(booking.paymentDeadline).toLocaleDateString('en-GB', {
                                        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                                      })}
                                    </p>
                                  )}
                                </div>
                                {isApprovalRequest && (
                                  <div className="flex gap-2">
                                    <Button size="sm" className="bg-green-600" onClick={(e) => { e.stopPropagation(); handleApprovalDecision(booking._id, 'approve'); }}>
                                      Accept
                                    </Button>
                                    <Button size="sm" variant="outline" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleApprovalDecision(booking._id, 'decline'); }}>
                                      Decline
                                    </Button>
                                  </div>
                                )}
                                {booking.status === 'pending' && (
                                  <div className="flex gap-2">
                                    <Button size="sm" className="bg-green-600" onClick={(e) => { e.stopPropagation(); handleBookingAction(booking._id, 'confirmed'); }}>
                                      Accept
                                    </Button>
                                    <Button size="sm" variant="outline" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleBookingAction(booking._id, 'cancelled'); }}>
                                      Decline
                                    </Button>
                                  </div>
                                )}
                                {isAwaitingPayment && (
                                  <div className="text-right">
                                    <p className="text-lg font-bold text-[#1F3A68]">£{booking.pricing?.total || 0}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-[#1F3A68]">Recent Activity</CardTitle>
                    {dismissedEvents.size > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-gray-500 hover:text-gray-700"
                        onClick={() => {
                          setDismissedEvents(new Set());
                          localStorage.removeItem('bc_dismissed_activity');
                        }}
                      >
                        Restore all
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const now = new Date();
                      const events = [];

                      bookings.forEach(booking => {
                        const checkIn = booking.checkIn ? new Date(booking.checkIn) : null;
                        const checkOut = booking.checkOut ? new Date(booking.checkOut) : null;
                        const createdAt = booking.createdAt ? new Date(booking.createdAt) : null;
                        const daysToCheckIn = checkIn ? Math.ceil((checkIn - now) / (86400000)) : null;
                        const daysToCheckOut = checkOut ? Math.ceil((checkOut - now) / (86400000)) : null;

                        // Check-in today or tomorrow
                        if (booking.status === 'confirmed' && daysToCheckIn !== null && daysToCheckIn >= 0 && daysToCheckIn <= 2) {
                          events.push({
                            id: `ci-${booking._id}`,
                            bookingId: booking._id,
                            icon: <Calendar size={16} className="text-blue-600" />,
                            bg: 'bg-blue-50',
                            border: 'border-blue-200',
                            hoverBg: 'hover:bg-blue-100',
                            title: daysToCheckIn === 0 ? 'Check-in today' : daysToCheckIn === 1 ? 'Check-in tomorrow' : 'Check-in in 2 days',
                            detail: `${booking.guestName} — ${booking.propertyTitle}`,
                            sub: `${booking.numberOfGuests || 1} guest${(booking.numberOfGuests || 1) > 1 ? 's' : ''} • ${booking.checkIn} to ${booking.checkOut}`,
                            priority: 0,
                            date: checkIn
                          });
                        }

                        // Payment pending
                        if (booking.status === 'pending_payment') {
                          events.push({
                            id: `pp-${booking._id}`,
                            bookingId: booking._id,
                            icon: <CreditCard size={16} className="text-orange-600" />,
                            bg: 'bg-orange-50',
                            border: 'border-orange-200',
                            hoverBg: 'hover:bg-orange-100',
                            title: 'Payment pending',
                            detail: `${booking.guestName} — ${booking.propertyTitle}`,
                            sub: booking.paymentDeadline
                              ? `Due: ${new Date(booking.paymentDeadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                              : `£${booking.pricing?.total || 0} • ${booking.checkIn}`,
                            priority: 1,
                            date: createdAt || now
                          });
                        }

                        // Check-out today or tomorrow
                        if (booking.status === 'confirmed' && daysToCheckOut !== null && daysToCheckOut >= 0 && daysToCheckOut <= 2) {
                          events.push({
                            id: `co-${booking._id}`,
                            bookingId: booking._id,
                            icon: <Users size={16} className="text-purple-600" />,
                            bg: 'bg-purple-50',
                            border: 'border-purple-200',
                            hoverBg: 'hover:bg-purple-100',
                            title: daysToCheckOut === 0 ? 'Check-out today' : daysToCheckOut === 1 ? 'Check-out tomorrow' : 'Check-out in 2 days',
                            detail: `${booking.guestName} — ${booking.propertyTitle}`,
                            priority: 2,
                            date: checkOut
                          });
                        }

                        // New bookings (created in last 7 days)
                        if (createdAt && (now - createdAt) < 7 * 86400000 && ['confirmed', 'pending', 'pending_payment'].includes(booking.status)) {
                          events.push({
                            id: `nb-${booking._id}`,
                            bookingId: booking._id,
                            icon: <Bell size={16} className="text-green-600" />,
                            bg: 'bg-green-50',
                            border: 'border-green-200',
                            hoverBg: 'hover:bg-green-100',
                            title: booking.status === 'confirmed' ? 'Booking confirmed' : 'New reservation',
                            detail: `${booking.guestName} — ${booking.propertyTitle}`,
                            sub: `£${booking.pricing?.total || 0} • ${booking.checkIn} to ${booking.checkOut}`,
                            priority: 3,
                            date: createdAt
                          });
                        }

                        // Cancellations (recent)
                        if (booking.status === 'cancelled' && createdAt && (now - createdAt) < 14 * 86400000) {
                          events.push({
                            id: `cx-${booking._id}`,
                            bookingId: booking._id,
                            icon: <X size={16} className="text-red-600" />,
                            bg: 'bg-red-50',
                            border: 'border-red-200',
                            hoverBg: 'hover:bg-red-100',
                            title: 'Booking cancelled',
                            detail: `${booking.guestName} — ${booking.propertyTitle}`,
                            sub: `${booking.checkIn} to ${booking.checkOut}`,
                            priority: 4,
                            date: createdAt
                          });
                        }
                      });

                      // Sort by priority, then by date
                      events.sort((a, b) => a.priority - b.priority || (b.date || 0) - (a.date || 0));

                      // Deduplicate
                      const seen = new Set();
                      const unique = events.filter(e => {
                        if (seen.has(e.id)) return false;
                        seen.add(e.id);
                        return true;
                      });

                      // Filter out dismissed events
                      const display = unique.filter(e => !dismissedEvents.has(e.id));

                      if (display.length === 0) {
                        return <p className="text-gray-500 text-center py-4">No recent activity</p>;
                      }

                      return (
                        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                          {display.map(event => (
                            <div
                              key={event.id}
                              className={`flex items-start gap-3 p-3 rounded-lg border ${event.bg} ${event.border} ${event.hoverBg} cursor-pointer transition-all group`}
                              onClick={() => navigateToBooking(event.bookingId)}
                              title="Click to view booking details"
                            >
                              <div className="mt-0.5 flex-shrink-0">{event.icon}</div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900">{event.title}</p>
                                <p className="text-sm text-gray-600 truncate">{event.detail}</p>
                                {event.sub && <p className="text-xs text-gray-500 mt-0.5">{event.sub}</p>}
                              </div>
                              <button
                                className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-white/60"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  dismissEvent(event.id);
                                }}
                                title="Dismiss"
                              >
                                <X size={14} className="text-gray-400 hover:text-gray-600" />
                              </button>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Properties Tab */}
          <TabsContent value="properties">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-[#1F3A68]">Your Properties</CardTitle>
                <Button className="bg-[#1F3A68] hover:bg-[#1F3A68]/90" onClick={() => navigate('/host/create-property')}>
                  <Plus size={16} className="mr-2" />
                  Add New Property
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3A68] mx-auto"></div>
                  </div>
                ) : properties.length === 0 ? (
                  <div className="text-center py-12">
                    <Home size={64} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="text-2xl font-bold text-gray-700 mb-2">No Properties Yet</h3>
                    <p className="text-gray-600 mb-6">Create your first property listing</p>
                    <Button className="bg-[#1F3A68] hover:bg-[#1F3A68]/90" onClick={() => navigate('/host/create-property')}>
                      <Plus size={16} className="mr-2" />
                      Create Property
                    </Button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {properties.map(property => (
                      <Card key={property._id} className="overflow-hidden">
                        {property.images?.length > 0 ? (
                          <img src={property.images[0]} alt={property.title} className="w-full h-48 object-cover" />
                        ) : (
                          <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                            <Home size={48} className="text-gray-400" />
                          </div>
                        )}
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-bold text-lg flex-1 line-clamp-1">{property.title}</h3>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              property.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {property.status}
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm mb-3">{property.location?.city}</p>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-2xl font-bold text-[#1F3A68]">£{property.pricing?.nightlyRate || 0}</span>
                            <span className="text-sm text-gray-600">/ night</span>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/property/${property._id}`)}>
                              <Eye size={14} className="mr-1" />
                              View
                            </Button>
                            <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/host/edit-property/${property._id}`)}>
                              <Edit2 size={14} className="mr-1" />
                              Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleToggleStatus(property._id, property.status)}>
                              {property.status === 'published' ? 'Unpublish' : 'Publish'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDeleteProperty(property._id)}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                          {/* Dispatch & Smart Lock Settings */}
                          <PropertyDispatchSettings
                            property={property}
                            token={getAuthToken()}
                            toast={toast}
                            onUpdate={fetchData}
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bookings Tab with Sub-navigation */}
          <TabsContent value="bookings">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-[#1F3A68]">Bookings & Calendar</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant={bookingsView === 'list' ? 'default' : 'outline'}
                      onClick={() => setBookingsView('list')}
                      className={bookingsView === 'list' ? 'bg-[#1F3A68]' : ''}
                      size="sm"
                    >
                      List View
                    </Button>
                    <Button
                      variant={bookingsView === 'calendar' ? 'default' : 'outline'}
                      onClick={() => setBookingsView('calendar')}
                      className={bookingsView === 'calendar' ? 'bg-[#1F3A68]' : ''}
                      size="sm"
                    >
                      Calendar View
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {bookingsView === 'list' ? (
                  // List View
                  bookings.length === 0 ? (
                    <div className="text-center py-12">
                      <Calendar size={64} className="mx-auto text-gray-300 mb-4" />
                      <h3 className="text-2xl font-bold text-gray-700 mb-2">No Bookings Yet</h3>
                      <p className="text-gray-600">When guests book your properties, they'll appear here.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* RTR Compliance Banner */}
                      {bookings.filter(b => b.rtrCheckRequired && !b.rtrCheckCompleted && ['confirmed', 'pending_payment', 'pending'].includes(b.status)).length > 0 && (
                        <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                          <h4 className="font-bold text-red-800 flex items-center gap-2 mb-2">
                            <AlertTriangle size={18} /> Right-to-Rent Checks Required
                          </h4>
                          {bookings.filter(b => b.rtrCheckRequired && !b.rtrCheckCompleted && ['confirmed', 'pending_payment', 'pending'].includes(b.status)).map(b => (
                            <div key={b._id} className="flex items-center justify-between py-2 border-t border-red-200 first:border-t-0">
                              <div className="text-sm text-red-700">
                                <span className="font-medium">{b.bookingRef}</span> — {b.guestName} — {b.nights} nights
                              </div>
                              <Button
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await bookingAPI.completeRtrCheck(b._id, getAuthToken());
                                    toast({ title: 'RTR Complete', description: 'Right-to-Rent check marked as completed' });
                                    fetchData();
                                  } catch (err) {
                                    toast({ title: 'Error', description: 'Failed to update RTR check', variant: 'destructive' });
                                  }
                                }}
                              >
                                Mark RTR Complete
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      {bookings.map(booking => (
                        <div
                          key={booking._id}
                          className={`flex items-center justify-between p-4 bg-white rounded-lg border hover:border-[#1F3A68] hover:shadow-md transition-all cursor-pointer group ${
                            highlightedBookingId === booking._id
                              ? 'ring-2 ring-[#1F3A68] ring-offset-2 border-[#1F3A68] shadow-lg animate-pulse'
                              : ''
                          }`}
                          onClick={() => { setSelectedBooking(booking); setShowBookingDetail(true); setHighlightedBookingId(null); }}
                        >
                          <div className="flex items-center gap-4">
                            {booking.propertyImage && (
                              <img src={booking.propertyImage} alt="" className="w-16 h-16 object-cover rounded" />
                            )}
                            <div>
                              <div className="font-bold text-gray-900">{booking.propertyTitle}</div>
                              <div className="text-gray-600 text-sm">Guest: {booking.guestName}</div>
                              <div className="text-gray-500 text-sm">{booking.checkIn} - {booking.checkOut}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <div className="text-xl font-bold text-[#1F3A68] mb-1">£{booking.pricing?.total}</div>
                              <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                                booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                                booking.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                booking.status === 'pending_payment' ? 'bg-blue-100 text-blue-700' :
                                booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                booking.status === 'expired' ? 'bg-gray-100 text-gray-500' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {booking.status === 'pending_payment' ? 'Awaiting Payment' : booking.status}
                              </span>
                            </div>
                            <ChevronRight size={20} className="text-gray-400 group-hover:text-[#1F3A68] transition-colors" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  // Calendar View
                  <div className="space-y-4">
                    {properties.length > 0 && (
                      <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                        <Label className="font-semibold">Select Property:</Label>
                        <select
                          value={selectedProperty?._id || ''}
                          onChange={(e) => setSelectedProperty(properties.find(p => p._id === e.target.value))}
                          className="h-10 px-3 border rounded-lg flex-1 max-w-md"
                        >
                          {properties.map(p => (
                            <option key={p._id} value={p._id}>{p.title}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {properties.length === 0 ? (
                      <div className="text-center py-12">
                        <Calendar size={64} className="mx-auto text-gray-300 mb-4" />
                        <h3 className="text-2xl font-bold text-gray-700 mb-2">No Properties</h3>
                        <p className="text-gray-600 mb-4">Create a property to manage its calendar</p>
                        <Button className="bg-[#1F3A68]" onClick={() => navigate('/host/create-property')}>
                          Create Property
                        </Button>
                      </div>
                    ) : selectedProperty ? (
                      <PropertyCalendar propertyId={selectedProperty._id} propertyTitle={selectedProperty.title} />
                    ) : (
                      <p className="text-center text-gray-500 py-8">Select a property to view its calendar</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle className="text-[#1F3A68]">Messages</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <HostMessagingPanel
                  autoSelectConversationId={searchParams.get('conversationId')}
                  onUnreadChange={handleUnreadChange}
                  refreshKey={messagesRefreshKey}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <TaskManagement onNavigateToMessages={(conversationId) => {
              setSearchParams({ tab: 'messages', conversationId }, { replace: false });
            }} />
          </TabsContent>

          {/* Deals Tab */}
          <TabsContent value="deals" data-testid="deals-content">
            <DealSourcing />
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts" data-testid="payouts-content">
            <PayoutsTab />
          </TabsContent>

          {/* Inventory Tab */}
          <TabsContent value="inventory">
            <InventoryManagement />
          </TabsContent>

          {/* Financial Insights Tab */}
          <TabsContent value="insights">
            <FinancialInsights onNavigate={(action, data) => {
              if (action === 'View Inventory' || action === 'View Alerts') {
                setActiveTab('inventory');
              } else if (action === 'Edit Property' && data?.propertyId) {
                navigate(`/host/edit-property/${data.propertyId}`);
              } else if (action === 'View Bookings') {
                setActiveTab('bookings');
              }
            }} />
          </TabsContent>

          {/* Refer & Earn Tab */}
          <TabsContent value="referrals">
            <ReferralDashboard />
          </TabsContent>

          <TabsContent value="faq">
            <FAQFeedback />
          </TabsContent>

        </Tabs>
      </div>

      {/* Booking Detail Modal */}
      <Dialog open={showBookingDetail} onOpenChange={setShowBookingDetail}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1F3A68] text-xl">Booking Details</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-6">
              {/* Property Info */}
              <div className="flex items-start gap-4">
                {selectedBooking.propertyImage && (
                  <img src={selectedBooking.propertyImage} alt="" className="w-24 h-24 object-cover rounded-lg" />
                )}
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-gray-900">{selectedBooking.propertyTitle}</h3>
                  <p className="text-gray-500 text-sm mt-1">Booking ID: {selectedBooking._id}</p>
                  <span className={`inline-block mt-2 text-xs px-3 py-1 rounded-full font-semibold ${
                    selectedBooking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                    selectedBooking.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    selectedBooking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    selectedBooking.status === 'pending_payment' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {selectedBooking.status?.replace('_', ' ')}
                  </span>
                </div>
              </div>

              {/* Guest Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-[#1F3A68] mb-3 flex items-center gap-2">
                  <User size={16} /> Guest Information
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Name:</span> <span className="font-medium">{selectedBooking.guestName || 'N/A'}</span></div>
                  <div><span className="text-gray-500">Email:</span> <span className="font-medium">{selectedBooking.guestEmail || 'N/A'}</span></div>
                  <div><span className="text-gray-500">Guests:</span> <span className="font-medium">{selectedBooking.numberOfGuests || 1}</span></div>
                </div>
              </div>

              {/* Stay Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-[#1F3A68] mb-3 flex items-center gap-2">
                  <Calendar size={16} /> Stay Details
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Check-in:</span> <span className="font-medium">{selectedBooking.checkIn}</span></div>
                  <div><span className="text-gray-500">Check-out:</span> <span className="font-medium">{selectedBooking.checkOut}</span></div>
                  <div><span className="text-gray-500">Nights:</span> <span className="font-medium">{selectedBooking.pricing?.nights || 'N/A'}</span></div>
                </div>
                {selectedBooking.specialRequests && (
                  <div className="mt-3 pt-3 border-t">
                    <span className="text-gray-500 text-sm">Special Requests:</span>
                    <p className="text-sm mt-1">{selectedBooking.specialRequests}</p>
                  </div>
                )}
              </div>

              {/* Pricing Breakdown */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-[#1F3A68] mb-3 flex items-center gap-2">
                  <DollarSign size={16} /> Pricing Breakdown
                </h4>
                <div className="space-y-2 text-sm">
                  {selectedBooking.pricing?.nightlyRate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Nightly Rate</span>
                      <span>£{selectedBooking.pricing.nightlyRate}</span>
                    </div>
                  )}
                  {selectedBooking.pricing?.accommodation && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Accommodation</span>
                      <span>£{selectedBooking.pricing.accommodation}</span>
                    </div>
                  )}
                  {selectedBooking.pricing?.cleaningFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cleaning Fee</span>
                      <span>£{selectedBooking.pricing.cleaningFee}</span>
                    </div>
                  )}
                  {selectedBooking.pricing?.serviceFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Service Fee</span>
                      <span>£{selectedBooking.pricing.serviceFee}</span>
                    </div>
                  )}
                  {selectedBooking.pricing?.vat > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">VAT ({selectedBooking.pricing.vatRate || 20}%)</span>
                      <span>£{selectedBooking.pricing.vat}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-base border-t pt-2 mt-2">
                    <span>Total</span>
                    <span className="text-[#1F3A68]">£{selectedBooking.pricing?.total || 0}</span>
                  </div>
                </div>
              </div>

              {/* Operations & Logistics */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-[#1F3A68] mb-3 flex items-center gap-2">
                  <ClipboardList size={16} /> Operations & Logistics
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Cleaning Status:</span>{' '}
                    <span className={`font-medium ${
                      selectedBooking.operations?.cleaningStatus === 'completed' ? 'text-green-600' :
                      selectedBooking.operations?.cleaningStatus === 'scheduled' ? 'text-blue-600' :
                      'text-gray-400'
                    }`}>
                      {selectedBooking.operations?.cleaningStatus
                        ? selectedBooking.operations.cleaningStatus.charAt(0).toUpperCase() + selectedBooking.operations.cleaningStatus.slice(1)
                        : 'Not assigned'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Cleaner / Company:</span>{' '}
                    <span className="font-medium">{selectedBooking.operations?.cleanerName || 'Not assigned'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Cleaning Date:</span>{' '}
                    <span className="font-medium">{selectedBooking.operations?.cleaningDate || 'TBD'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Inventory Used:</span>{' '}
                    <span className="font-medium">
                      {selectedBooking.operations?.inventoryItems?.length > 0
                        ? selectedBooking.operations.inventoryItems.map(i => i.name || i).join(', ')
                        : 'None recorded'}
                    </span>
                  </div>
                </div>
                {selectedBooking.operations?.notes && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <span className="text-gray-500 text-sm">Notes:</span>
                    <p className="text-sm mt-1 text-gray-700">{selectedBooking.operations.notes}</p>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 w-full text-[#1F3A68] border-[#1F3A68]/30 hover:bg-[#1F3A68]/5"
                  onClick={() => {
                    toast({ title: 'Coming Soon', description: 'Full operations management — cleaning schedules, inventory tracking, and vendor assignments — will be available in a future update.' });
                  }}
                >
                  <Package size={14} className="mr-1" />
                  Manage Operations
                </Button>
              </div>

              {/* Actions */}
              {selectedBooking.status === 'pending' && (
                <div className="flex gap-3">
                  <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => { handleBookingAction(selectedBooking._id, 'confirmed'); setShowBookingDetail(false); }}>
                    Accept Booking
                  </Button>
                  <Button variant="outline" className="flex-1 text-red-600 border-red-300 hover:bg-red-50" onClick={() => { handleBookingAction(selectedBooking._id, 'cancelled'); setShowBookingDetail(false); }}>
                    Decline Booking
                  </Button>
                </div>
              )}
              {/* Message Guest — available for confirmed, pending_payment, and cancelled bookings */}
              {['confirmed', 'pending_payment', 'pending', 'cancelled'].includes(selectedBooking.status) && (
                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1" onClick={async () => {
                    setShowBookingDetail(false);
                    try {
                      const token = getAuthToken();
                      // Create or find conversation for this booking — pass guestId so it goes to the guest, not the host
                      const conv = await messagesAPI.createConversation({
                        propertyId: selectedBooking.propertyId,
                        bookingId: selectedBooking._id,
                        guestId: selectedBooking.guestId
                      }, token);
                      setSearchParams({ tab: 'messages', conversationId: conv._id }, { replace: false });
                    } catch (err) {
                      setSearchParams({ tab: 'messages' }, { replace: false });
                    }
                  }}>
                    <MessageSquare size={16} className="mr-2" /> Message Guest
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Analytics Slide-Out Drawer */}
      {showAnalyticsDrawer && (
        <div className="fixed inset-0 z-[60]">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAnalyticsDrawer(false)} />
          {/* Drawer */}
          <div className="absolute top-0 right-0 h-full w-full max-w-5xl bg-[#F4F2E9] shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
            <div className="sticky top-0 z-10 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BarChart3 size={24} className="text-[#1F3A68]" />
                <h2 className="text-xl font-bold text-[#1F3A68]">Analytics Dashboard</h2>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowAnalyticsDrawer(false)}>
                <X size={24} />
              </Button>
            </div>
            <div className="p-6">
              <AnalyticsDashboard />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Property Dispatch Settings inline component
const PropertyDispatchSettings = ({ property, token, toast, onUpdate }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [cleaners, setCleaners] = React.useState([]);
  const [jobDate, setJobDate] = React.useState('');
  const [jobCleaner, setJobCleaner] = React.useState('');
  const [creatingJob, setCreatingJob] = React.useState(false);

  const autoDispatch = property.auto_dispatch_cleaning || false;
  const linenHire = property.linenHireEnabled || false;
  const smartLock = property.hasSmartLock || false;
  const autoClean = property.autoCleanOnExternalTurnover || false;
  const preferredCleanerId = property.preferredCleanerId || '';
  const backupCleanerId = property.backupCleanerId || '';

  React.useEffect(() => {
    if (!expanded || cleaners.length > 0) return;
    (async () => {
      try {
        const list = await tasksAPI.getCleaners(token);
        setCleaners(Array.isArray(list) ? list : []);
      } catch {
        /* leave empty — the picker will prompt to add a cleaner */
      }
    })();
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateDispatch = async (field, value) => {
    setSaving(true);
    try {
      await propertyAPI.updateDispatchSettings(property._id, { [field]: value }, token);
      toast({ title: 'Updated', description: `${field} updated` });
      if (onUpdate) onUpdate();
    } catch {
      toast({ title: 'Error', description: 'Failed to update setting', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const createOneOffJob = async () => {
    if (!jobDate) {
      toast({ title: 'Pick a date', description: 'Choose the checkout date to clean after.', variant: 'destructive' });
      return;
    }
    setCreatingJob(true);
    try {
      await cleaningAPI.createJob(
        { propertyId: property._id, checkoutDate: jobDate, cleanerId: jobCleaner || undefined },
        token
      );
      toast({ title: 'Cleaner notified', description: 'We emailed your cleaner to accept or decline.' });
      setJobDate('');
      setJobCleaner('');
      if (onUpdate) onUpdate();
    } catch (e) {
      toast({
        title: 'Could not create the job',
        description: e?.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCreatingJob(false);
    }
  };

  const toggleSmartLock = async (value) => {
    setSaving(true);
    try {
      await propertyAPI.toggleSmartLock(property._id, value, token);
      toast({ title: 'Updated', description: `Smart Lock ${value ? 'enabled' : 'disabled'}` });
      if (onUpdate) onUpdate();
    } catch {
      toast({ title: 'Error', description: 'Failed to update Smart Lock', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 border-t pt-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-xs text-[#1F3A68] hover:underline flex items-center gap-1"
      >
        <Settings size={12} />
        {expanded ? 'Hide' : 'Show'} Dispatch & Smart Lock Settings
        <ChevronRight size={12} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="mt-2 space-y-2 text-sm" style={{ opacity: saving ? 0.6 : 1 }}>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={autoDispatch}
              onChange={(e) => updateDispatch('auto_dispatch_cleaning', e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span>Auto-dispatch cleaning on checkout</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={linenHire}
              onChange={(e) => updateDispatch('linenHireEnabled', e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span>Linen hire (PIN lock on cleaning)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={smartLock}
              onChange={(e) => toggleSmartLock(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span>Smart Lock enabled (0.5% commission discount)</span>
          </label>

          {/* External-turnover cleaning: works even when the guest booked
              elsewhere (Airbnb/Booking.com) and the stay reaches Ariv via the
              synced calendar. Offers the clean to your own cleaner and cascades
              to a backup if they can't make it. */}
          <div className="pt-2 mt-1 border-t border-slate-100">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoClean}
                onChange={(e) => updateDispatch('autoCleanOnExternalTurnover', e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span>Auto-offer cleaning on external turnovers (Airbnb, Booking.com…)</span>
            </label>

            {autoClean && (
              <div className="mt-2 pl-6 space-y-2">
                {cleaners.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    Add cleaners in your Cleaners list first, then pick who to offer jobs to.
                  </p>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-16">Preferred</span>
                      <select
                        value={preferredCleanerId}
                        onChange={(e) => updateDispatch('preferredCleanerId', e.target.value)}
                        className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
                      >
                        <option value="">— choose a cleaner —</option>
                        {cleaners.map((c) => (
                          <option key={c._id} value={c._id}>{c.name}{c.email ? '' : ' (no email)'}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-16">Backup</span>
                      <select
                        value={backupCleanerId}
                        onChange={(e) => updateDispatch('backupCleanerId', e.target.value)}
                        className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
                      >
                        <option value="">— none —</option>
                        {cleaners.map((c) => (
                          <option key={c._id} value={c._id}>{c.name}{c.email ? '' : ' (no email)'}</option>
                        ))}
                      </select>
                    </div>
                    <p className="text-[11px] text-slate-400">
                      Your cleaner gets an email to Accept or Decline. If they decline or don't
                      answer in 12 hours, Ariv offers it to your backup. If nobody takes it, we tell you.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* One-off: book a cleaner for a turnover you already know about, even
              when the booking came from another site. This is the "create a
              cleaning order and pick a cleaner" flow, and runs the same
              Accept/Decline/backup loop. */}
          <div className="pt-2 mt-1 border-t border-slate-100">
            <p className="font-medium text-slate-700 mb-2">Book a one-off clean</p>
            {cleaners.length === 0 ? (
              <p className="text-xs text-slate-500">Add a cleaner to your Cleaners list first.</p>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-16">Checkout</span>
                  <input
                    type="date"
                    value={jobDate}
                    onChange={(e) => setJobDate(e.target.value)}
                    className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-16">Cleaner</span>
                  <select
                    value={jobCleaner}
                    onChange={(e) => setJobCleaner(e.target.value)}
                    className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="">Preferred cleaner</option>
                    {cleaners.map((c) => (
                      <option key={c._id} value={c._id}>{c.name}{c.email ? '' : ' (no email)'}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={createOneOffJob}
                  disabled={creatingJob}
                  className="w-full bg-[#1F3A68] text-white py-2 rounded-lg text-sm font-semibold hover:bg-[#1F3A68]/90 disabled:opacity-50"
                >
                  {creatingJob ? 'Sending…' : 'Offer this clean'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HostDashboard;
